import {Button, Group, Modal, Stack, Text, Tooltip} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {IconMail} from '@tabler/icons-react'
import {useState} from 'react'

import {quoteLabel} from './quote-email'
import type {Quote, QuoteStatus} from './schemas'
import {useEmailQuote} from './use-quotes'

type QuoteEmailButtonProps = {
    quoteId: string
    savedQuote: Quote | undefined
    blockedReason: string | null
    onEmailed: (status: QuoteStatus) => void
}

export function QuoteEmailButton({
    quoteId,
    savedQuote,
    blockedReason,
    onEmailed,
}: QuoteEmailButtonProps) {
    const emailQuoteMutation = useEmailQuote(quoteId)
    const [opened, setOpened] = useState(false)
    const emailError = emailQuoteMutation.isError
        ? emailQuoteMutation.error instanceof Error
            ? emailQuoteMutation.error.message
            : 'Failed to email quote'
        : null

    function handleEmail() {
        emailQuoteMutation.mutate(undefined, {
            onSuccess: (updated) => {
                onEmailed(updated.status)
                setOpened(false)
                notifications.show({
                    title: 'Quote emailed',
                    message: `Sent to ${updated.customerContact?.name ?? 'the customer'}.`,
                    color: 'green',
                })
            },
        })
    }

    return (
        <>
            <Tooltip label={blockedReason} disabled={blockedReason == null} multiline w={260}>
                <Button
                    variant="light"
                    leftSection={<IconMail size={16} stroke={1.5} />}
                    // data-disabled keeps the tooltip active
                    data-disabled={blockedReason != null || undefined}
                    aria-disabled={blockedReason != null}
                    onClick={(event) => {
                        if (blockedReason != null) {
                            event.preventDefault()
                            return
                        }
                        emailQuoteMutation.reset()
                        setOpened(true)
                    }}
                >
                    Email quote
                </Button>
            </Tooltip>
            <Modal opened={opened} onClose={() => setOpened(false)} title="Email quote" centered>
                <Stack gap="md">
                    <Text size="sm">
                        Email quote {quoteLabel({id: quoteId, name: savedQuote?.name ?? null})} as a
                        PDF to {savedQuote?.customerContact?.name} (
                        {savedQuote?.customerContact?.email}), copying{' '}
                        {savedQuote?.createdByName ?? 'the rep'}?
                        {savedQuote?.status === 'draft' ? ' The quote will be marked sent.' : ''}
                    </Text>
                    {emailError ? (
                        <Text size="sm" c="red">
                            {emailError}
                        </Text>
                    ) : null}
                    <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={() => setOpened(false)}>
                            Cancel
                        </Button>
                        <Button loading={emailQuoteMutation.isPending} onClick={handleEmail}>
                            Send email
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    )
}
